import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

const MAX_ROWS = 500;

export const isCsvLike = (fileName, contentType) => {
  const n = (fileName || "").toLowerCase();
  const t = (contentType || "").toLowerCase();
  return n.endsWith(".csv") || n.endsWith(".txt") || n.endsWith(".tsv") || t.includes("csv") || t.startsWith("text/plain") || t.includes("tab-separated");
};

const decode = (buf) => {
  const utf8 = new TextDecoder("utf-8").decode(buf);
  return utf8.includes("\uFFFD") ? new TextDecoder("windows-1252").decode(buf) : utf8;
};

const detectDelimiter = (line) => {
  const counts = { ";": 0, ",": 0, "\t": 0, "|": 0 };
  for (const ch of line) if (ch in counts) counts[ch] += 1;
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
};

const parseLine = (line, delim) => {
  const out = [];
  let cur = "", quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (quoted && line[i + 1] === '"') { cur += '"'; i++; } else quoted = !quoted;
    } else if (c === delim && !quoted) { out.push(cur); cur = ""; } else cur += c;
  }
  out.push(cur);
  return out;
};

export const parseCsv = (text) => {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim() !== "");
  if (!lines.length) return { header: [], rows: [], total: 0, meta: [] };
  const delim = detectDelimiter(lines.slice(0, 10).sort((a, b) => b.length - a.length)[0]);
  const all = lines.map((l) => parseLine(l, delim));
  // Exporte mit Kopfzeilen (z.B. TWINT «Datum Überweisung;…»): breiteste Zeile = Spaltenkopf, Zeilen davor = Meta
  const maxCols = Math.max(...all.map((r) => r.length));
  const headerIdx = all.findIndex((r) => r.length === maxCols);
  const meta = all.slice(0, headerIdx).filter((r) => r[0]).map((r) => [r[0], r.slice(1).join(" ").trim()]);
  const body = all.slice(headerIdx + 1);
  return { header: all[headerIdx], rows: body.slice(0, MAX_ROWS), total: body.length, meta };
};

export const CsvPreview = ({ blobUrl, fileName }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null);
    setError(false);
    fetch(blobUrl).then((r) => r.arrayBuffer()).then((buf) => { if (active) setData(parseCsv(decode(buf))); }).catch(() => active && setError(true));
    return () => { active = false; };
  }, [blobUrl]);

  if (error) return <div className="flex items-center justify-center h-full text-sm text-slate-500" data-testid="inbox-csv-error">CSV konnte nicht gelesen werden</div>;
  if (!data) return <div className="flex items-center justify-center h-full text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data.header.length) return <div className="flex items-center justify-center h-full text-sm text-slate-500" data-testid="inbox-csv-empty">Datei ist leer</div>;

  return (
    <div className="h-full flex flex-col" data-testid="inbox-csv-preview">
      <div className="px-3 py-2 text-xs text-slate-500 border-b border-slate-200 bg-slate-50 flex justify-between" data-testid="inbox-csv-meta">
        <span className="truncate">{fileName}</span>
        <span>{data.total} Zeilen · {data.header.length} Spalten{data.total > MAX_ROWS ? ` · erste ${MAX_ROWS} angezeigt` : ""}</span>
      </div>
      {data.meta?.length > 0 && (
        <dl className="px-3 py-2 border-b border-slate-200 bg-white grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-xs" data-testid="inbox-csv-meta-rows">
          {data.meta.map(([k, v], i) => <React.Fragment key={i}><dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-800">{v}</dd></React.Fragment>)}
        </dl>
      )}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full text-xs font-mono border-collapse">
          <thead className="sticky top-0 bg-slate-100">
            <tr>{data.header.map((h, i) => <th key={i} className="text-left px-2 py-1.5 border-b border-r border-slate-200 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.rows.map((row, ri) => (
              <tr key={ri} className="odd:bg-white even:bg-slate-50/60 hover:bg-amber-50" data-testid={`inbox-csv-row-${ri}`}>
                {data.header.map((_, ci) => <td key={ci} className="px-2 py-1 border-b border-r border-slate-100 whitespace-nowrap">{row[ci] ?? ""}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
